/**
 * Restore saved game state - load scenes and restore bubbles/choices.
 */

import type { SaveState } from '../save-state'
import type { SceneData, ChoiceCondition } from '../scenes/types'
import type { StoryScene } from '../../components/StoryScene'
import { filterBubblesByCondition } from '../scenes/speech-bubbles/bubble-filter'

export interface RestoreStateContext {
  container: HTMLElement
  scenes: SceneData[]
  loadedScenes: Map<string, StoryScene>
  loadScene: (sceneIndex: number, animate: boolean) => void
  shouldLoadChoiceScene: (sceneId: string, savedChoices: Record<string, number>) => boolean
  evaluateCondition: (condition: ChoiceCondition) => boolean
  setRestoringFlag: (value: boolean) => void
}

/** Load all scenes up to the saved index (skip choice scenes unless they match saved choices). */
function loadScenesUpToSavedIndex(state: SaveState, ctx: RestoreStateContext): void {
  if (state.lastLoadedScene.index < 0) return
  const maxIndex = Math.min(state.lastLoadedScene.index, ctx.scenes.length - 1)
  const savedChoices = state.choices ?? {}

  for (let i = 0; i <= maxIndex; i++) {
    const sceneData = ctx.scenes[i]
    if (!sceneData) continue
    if (sceneData.type === 'choice') {
      if (!ctx.shouldLoadChoiceScene(sceneData.id, savedChoices)) continue
    }
    if (sceneData.when && !ctx.evaluateCondition(sceneData.when)) continue
    ctx.loadScene(i, false)
  }
}

/** Restore bubble and choice state for every loaded scene, then clear flag and call onComplete. */
function restoreBubblesAndChoicesThenComplete(
  state: SaveState,
  ctx: RestoreStateContext,
  onComplete?: () => void,
): void {
  const lastSceneWithBubbleIndex =
    state.lastLoadedScene.bubble !== undefined ? ctx.scenes.findIndex((s) => s.id === state.lastLoadedScene.id) : -1

  if (state.lastLoadedScene.index >= 0) {
    for (let i = 0; i <= state.lastLoadedScene.index && i < ctx.scenes.length; i++) {
      const sceneData = ctx.scenes[i]
      const scene = sceneData ? ctx.loadedScenes.get(sceneData.id) : undefined
      if (!scene || !sceneData) continue

      if (sceneData.speechBubbles?.length) {
        const filtered = filterBubblesByCondition(sceneData.speechBubbles, (c) => ctx.evaluateCondition(c))
        const n = filtered.length
        if (n > 0 && i < lastSceneWithBubbleIndex) {
          scene.restoreState(filtered[n - 1].id, n - 1)
        } else if (i === lastSceneWithBubbleIndex && state.lastLoadedScene.bubble !== undefined) {
          const savedBubble = Math.min(state.lastLoadedScene.bubble, n) 
          scene.restoreState(`bubble-${savedBubble}`, savedBubble - 1)
        }
      }
      if (sceneData.choices && state.choices?.[sceneData.id] !== undefined) {
        scene.restoreChoiceState(state.choices[sceneData.id])
      }
    }
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Clear restoring before onComplete so listeners (e.g. minigame onComplete → loadNext) can save and load scenes.
      setTimeout(() => {
        ctx.setRestoringFlag(false)
        onComplete?.()
      }, 100)
    })
  })
}

/** Run callback once all img in container have loaded (or there are none), with a max timeout. */
function whenContainerImagesLoaded(container: HTMLElement, onReady: () => void): void {
  const MAX_WAIT_MS = 3000
  const images = container.querySelectorAll('img')
  const total = images.length
  if (total === 0) {
    onReady()
    return
  }
  let fired = false
  const fire = () => {
    if (fired) return
    fired = true
    onReady()
  }
  let loaded = 0
  const check = () => {
    loaded++
    if (loaded === total) fire()
  }
  images.forEach((img) => {
    if ((img as HTMLImageElement).complete) check()
    else {
      img.addEventListener('load', check, { once: true })
      img.addEventListener('error', check, { once: true })
    }
  })
  setTimeout(fire, MAX_WAIT_MS)
}

/**
 * Restore state: load scenes up to saved index, then restore bubbles and choices.
 */
export function restoreState(state: SaveState, ctx: RestoreStateContext, onComplete?: () => void): void {
  ctx.setRestoringFlag(true)
  loadScenesUpToSavedIndex(state, ctx)

  requestAnimationFrame(() => {
    setTimeout(() => {
      whenContainerImagesLoaded(ctx.container, () => {
        requestAnimationFrame(() => {
          restoreBubblesAndChoicesThenComplete(state, ctx, onComplete)
        })
      })
    }, 100)
  })
}
